/**
 * Deterministic seeded PRNG (mulberry32) + a string→seed hash.
 *
 * Determinism is load-bearing (D9): each day's routes/weather/encounters and the
 * overnight society tick are pure functions of `hash(worldSeed + inGameDate [+ route])`.
 * Same seed ⇒ identical sequence; `fork()` derives an independent, reproducible
 * sub-stream so layers don't perturb each other's rolls.
 *
 * mulberry32 is a fast, well-distributed 32-bit generator — plenty for game RNG
 * (it is NOT cryptographic; never use it for secrets).
 */

/** A seeded random number generator with a small, deterministic API. */
export interface Rng {
  /** Next float in [0, 1). */
  next(): number;
  /** Integer in [0, maxExclusive). Throws if maxExclusive < 1. */
  int(maxExclusive: number): number;
  /** Alias for next() — float in [0, 1). */
  float(): number;
  /** Float in [min, max). */
  range(min: number, max: number): number;
  /** Uniformly pick one element; throws on empty array. */
  pick<T>(arr: readonly T[]): T;
  /** Returns a new shuffled copy (Fisher–Yates); does not mutate the input. */
  shuffle<T>(arr: readonly T[]): T[];
  /** True with probability p (0–1). */
  chance(p: number): boolean;
  /**
   * Derive an independent child RNG, optionally salted. Deterministic: the same
   * parent state + label always yields the same fork, but the fork's stream is
   * decoupled from further calls on the parent.
   */
  fork(label?: string): Rng;
  /** Current internal state (for snapshotting / resuming a stream). */
  getState(): number;
}

/** mulberry32 core step over a 32-bit state. */
function mulberry32Step(state: number): { value: number; state: number } {
  let s = state | 0;
  s = (s + 0x6d2b79f5) | 0;
  let t = s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, state: s };
}

/** FNV-1a 32-bit hash of a string → a stable unsigned 32-bit seed. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * The canonical determinism anchor: combine the world seed with the in-game date
 * and an optional route/node id into a single stable seed.
 */
export function hash(worldSeed: number, inGameDate: string, route?: string): number {
  const key = `${worldSeed >>> 0}|${inGameDate}|${route ?? ''}`;
  // Mix the numeric seed in too, so two dates that hash alike still diverge by seed.
  return (hashString(key) ^ (worldSeed >>> 0)) >>> 0;
}

class Mulberry32 implements Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  next(): number {
    const { value, state } = mulberry32Step(this.state);
    this.state = state;
    return value;
  }

  float(): number {
    return this.next();
  }

  int(maxExclusive: number): number {
    if (!Number.isFinite(maxExclusive) || maxExclusive < 1) {
      throw new RangeError(`int(maxExclusive) requires maxExclusive >= 1, got ${maxExclusive}`);
    }
    return Math.floor(this.next() * Math.floor(maxExclusive));
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) {
      throw new RangeError('pick() called on an empty array');
    }
    return arr[this.int(arr.length)]!;
  }

  shuffle<T>(arr: readonly T[]): T[] {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = out[i]!;
      out[i] = out[j]!;
      out[j] = tmp;
    }
    return out;
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  fork(label = ''): Rng {
    // Deterministic child seed: mix current state with a hash of the label.
    const childSeed = (this.state ^ hashString(label) ^ 0x9e3779b9) >>> 0;
    // Advance the parent so repeated unlabeled forks differ.
    this.next();
    return new Mulberry32(childSeed === 0 ? 1 : childSeed);
  }

  getState(): number {
    return this.state >>> 0;
  }
}

/** Create a seeded RNG from a numeric seed. */
export function makeRng(seed: number): Rng {
  return new Mulberry32(seed >>> 0);
}

/** Create a seeded RNG from the canonical (seed, date, route) anchor. */
export function makeRngFor(worldSeed: number, inGameDate: string, route?: string): Rng {
  return makeRng(hash(worldSeed, inGameDate, route));
}
